import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';
import { io } from 'socket.io-client';

export const vehicleFilters = ['all', 'ساحبة', 'رافعة'];
export const driverStateFilters = ['all', 'active', 'inactive'];

export const useActivity = () => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();

    const [drivers, setDrivers] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [driverStateFilter, setDriverStateFilter] = useState('all');
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [maxSearchRangeKm, setMaxSearchRangeKm] = useState(null);
    const [notifyDriverIds, setNotifyDriverIds] = useState([]);
    const socketRef = useRef(null);

    const fetchActivity = useCallback(
        async (vehicleType = 'all', stateFilter = 'all', options = {}) => {
            if (!user?.token) return;
            const withLoader = options.withLoader !== false;
            try {
                if (withLoader) {
                    setLoading(true);
                }
                const params = new URLSearchParams();
                if (vehicleType && vehicleType !== 'all') {
                    params.append('vehicleType', vehicleType);
                }
                if (stateFilter && stateFilter !== 'all') {
                    params.append('state', stateFilter);
                }

                const url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/activity${
                    params.toString() ? `?${params.toString()}` : ''
                }`;

                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                });
                const json = await response.json();

                if (!response.ok) {
                    throw new Error(json.error || 'تعذر جلب بيانات النشاط');
                }

                setDrivers(json.drivers || []);
                setRequests(json.requests || []);
                setMaxSearchRangeKm(typeof json.maxSearchRangeKm === 'number' ? json.maxSearchRangeKm : null);
                setNotifyDriverIds(Array.isArray(json.notifyDriverIds) ? json.notifyDriverIds : []);
                if (selectedRequestId) {
                    const stillExists = (json.requests || []).some((r) => String(r._id) === String(selectedRequestId));
                    if (!stillExists) {
                        setSelectedRequestId(null);
                    }
                }
            } catch (error) {
                dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            } finally {
                if (withLoader) {
                    setLoading(false);
                }
            }
        },
        [user?.token, dispatch, selectedRequestId]
    );

    useEffect(() => {
        fetchActivity(filter, driverStateFilter);
    }, [fetchActivity, filter, driverStateFilter]);

    useEffect(() => {
        if (!selectedRequestId) return undefined;
        const selectedRequest = requests.find((request) => String(request._id) === String(selectedRequestId));
        if (!selectedRequest || selectedRequest.status !== 'accepted') return undefined;

        const tripState = String(selectedRequest.tripState || '').toLowerCase();
        if (tripState && ['completed', 'cancelled', 'faild'].includes(tripState)) {
            return undefined;
        }

        const timer = setInterval(() => {
            fetchActivity(filter, driverStateFilter, { withLoader: false });
        }, 30000);

        return () => clearInterval(timer);
    }, [selectedRequestId, requests, fetchActivity, filter, driverStateFilter]);

    const applyFilter = (nextFilter) => {
        setFilter(nextFilter || 'all');
    };

    const applyDriverStateFilter = (nextFilter) => {
        setDriverStateFilter(nextFilter || 'all');
    };

    const refresh = () => fetchActivity(filter, driverStateFilter);

    const redirectRequest = async (requestId, driverId) => {
        if (!user?.token) return false;
        if (!requestId || !driverId) return false;
        try {
            const url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/activity/redirect`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify({ requestId, driverId }),
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'تعذر إعادة توجيه الطلب للسائق');
            }
            // refresh to reflect any new invites/offers
            fetchActivity(filter, driverStateFilter);
            dispatch(alertActions.showAlert({ msg: 'تم إرسال إشعار للسائق المختار', type: 'success' }));
            return true;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return false;
        }
    };

    const updateNotifyList = async (driverIds = []) => {
        if (!user?.token) return false;
        try {
            const url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/activity/notify-list`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify({ driverIds }),
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'تعذر حفظ قائمة الإشعارات');
            }
            setNotifyDriverIds(Array.isArray(json.notifyDriverIds) ? json.notifyDriverIds : []);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث قائمة الإشعارات', type: 'success' }));
            return true;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return false;
        }
    };

    useEffect(() => {
        if (!user?.token) return undefined;
        const base = process.env.REACT_APP_API_BASE_URL;
        if (!base) return undefined;

        const socket = io(base, {
            transports: ['websocket'],
            auth: { token: user.token },
            reconnectionAttempts: 5,
        });
        socketRef.current = socket;

        const handleRefreshEvent = (payload = {}) => {
            // refresh everything; selected request will stay highlighted
            const shouldRefetch = !selectedRequestId || !payload.requestId || String(payload.requestId) === String(selectedRequestId);
            if (shouldRefetch) {
                fetchActivity(filter, driverStateFilter);
            }
        };

        socket.on('connect', () => {
            fetchActivity(filter, driverStateFilter);
        });

        socket.on('requests_refresh', handleRefreshEvent);
        socket.on('offers_updated', handleRefreshEvent);
        socket.on('offer_submitted', handleRefreshEvent);
        socket.on('offer_rejected', handleRefreshEvent);

        return () => {
            socket.off('requests_refresh', handleRefreshEvent);
            socket.off('offers_updated', handleRefreshEvent);
            socket.off('offer_submitted', handleRefreshEvent);
            socket.off('offer_rejected', handleRefreshEvent);
            socket.disconnect();
        };
    }, [user?.token, fetchActivity, filter, driverStateFilter, selectedRequestId]);

    const selectRequest = (requestId) => {
        setSelectedRequestId(requestId);
    };

    return {
        drivers,
        requests,
        loading,
        filter,
        driverStateFilter,
        applyFilter,
        applyDriverStateFilter,
        refresh,
        selectRequest,
        selectedRequestId,
        maxSearchRangeKm,
        redirectRequest,
        notifyDriverIds,
        updateNotifyList,
    };
};
